import {
  ParticipantInteractionType,
  ParticipantInteractionEffect,
  ParticipantGroupType,
  prisma,
  NetworkingType,
} from "client";
import { canAccessBoothAdminPortal, ensureCompanyOwnerPortalAccess, resolveCompanyTypeForUser } from "../team";
import type { TeamMemberPermissions } from "../team/owner";
import { applyCapabilityRules } from "./applyCapabilityRules.pure";

// --- CREATE a new interaction rule ---
export const setGlobalInteractionRule = async (
  sourceGroupId: string,
  interactionType: ParticipantInteractionType,
  effect: ParticipantInteractionEffect,
  privacy: NetworkingType
) => {
  try {
    if (!interactionType) {
      throw new Error("interactionType is required");
    }

    return await prisma.$transaction(async (tx) => {
      // Delete existing rules for this source and type
      await tx.participantGroupInteractionRule.deleteMany({
        where: { sourceGroupId, interactionType },
      });

      // Create a single global rule with no targetGroupId
      const rule = await tx.participantGroupInteractionRule.create({
        data: {
          sourceGroupId,
          interactionType,
          effect,
          networkingPrivacy: privacy,
        },
      });

      return [rule];
    });
  } catch (error) {
    console.error("Error setting global interaction rule:", error);
    throw error;
  }
};

// --- BULK SET rules with mixed effects (ALLOW/DENY) ---
export const setInteractionRulesWithEffects = async (
  eventId: string,
  sourceGroupId: string,
  interactionType: ParticipantInteractionType,
  allowTypes: ParticipantGroupType[],
  denyTypes: ParticipantGroupType[],
  privacy: NetworkingType
) => {
  try {
    if (!interactionType) {
      throw new Error("interactionType is required");
    }
    if (!Array.isArray(allowTypes) && !Array.isArray(denyTypes)) {
      throw new Error("allowTypes or denyTypes must be provided as arrays");
    }

    // Resolve target group IDs for allow and deny types
    const [allowGroups, denyGroups] = await Promise.all([
      prisma.participantGroup.findMany({
        where: {
          eventId,
          type: { in: allowTypes.length > 0 ? allowTypes : [] },
        },
        select: { id: true },
      }),
      prisma.participantGroup.findMany({
        where: { eventId, type: { in: denyTypes.length > 0 ? denyTypes : [] } },
        select: { id: true },
      }),
    ]);

    const allowIds = allowGroups.map((g) => g.id);
    const denyIds = denyGroups.map((g) => g.id);

    return await prisma.$transaction(async (tx) => {
      // Remove all existing rules for this source and type
      await tx.participantGroupInteractionRule.deleteMany({
        where: { sourceGroupId, interactionType },
      });

      const created: any[] = [];

      // Create ALLOW rules
      if (allowIds.length > 0) {
        const createdAllow = await Promise.all(
          allowIds.map((targetGroupId) =>
            tx.participantGroupInteractionRule.create({
              data: {
                sourceGroupId,
                targetGroupId,
                interactionType,
                effect: ParticipantInteractionEffect.ALLOW,
                networkingPrivacy: privacy,
              },
            })
          )
        );
        created.push(...createdAllow);
      }

      // Create DENY rules
      if (denyIds.length > 0) {
        const createdDeny = await Promise.all(
          denyIds.map((targetGroupId) =>
            tx.participantGroupInteractionRule.create({
              data: {
                sourceGroupId,
                targetGroupId,
                interactionType,
                effect: ParticipantInteractionEffect.DENY,
                networkingPrivacy: privacy,
              },
            })
          )
        );
        created.push(...createdDeny);
      }

      return created;
    });
  } catch (error) {
    console.error("Error setting interaction rules with effects:", error);
    throw error;
  }
};

// --- CREATE single interaction rule ---
export const createInteractionRule = async (
  sourceGroupId: string,
  targetGroupId: string,
  interactionType: ParticipantInteractionType,
  effect: ParticipantInteractionEffect,
  privacy: NetworkingType
) => {
  try {
    return await prisma.participantGroupInteractionRule.create({
      data: {
        sourceGroupId,
        targetGroupId,
        interactionType,
        effect,
        networkingPrivacy: privacy,
      },
    });
  } catch (error) {
    console.error("Error creating interaction rule:", error);
    throw error;
  }
};
export const getInteractionRulesBySourceGroup = async (
  sourceGroupId: string
) => {
  try {
    const rules = await prisma.participantGroupInteractionRule.findMany({
      where: {
        sourceGroupId,
      },
      include: {
        targetGroup: {
          select: {
            id: true,
            type: true,
            translations: true,
          },
        },
      },
      orderBy: [{ interactionType: "asc" }, { effect: "asc" }],
    });

    return rules;
  } catch (error) {
    console.error("Error fetching interaction rules:", error);
    throw error;
  }
};
// --- GET an interaction rule by ID ---
export const findRuleById = async (ruleId: string) => {
  try {
    const rule = await prisma.participantGroupInteractionRule.findUnique({
      where: {
        id: ruleId,
      },
    });
    return rule;
  } catch (error) {
    console.error("Error finding rule by ID:", error);
    throw error;
  }
};

// --- GET all interaction rules ---
export const findParticipantGroupInteractionRules = async (filter = {}) => {
  try {
    const rules = await prisma.participantGroupInteractionRule.findMany({
      where: filter,
      include: {
        sourceGroup: true,
        targetGroup: true,
      },
    });
    return rules;
  } catch (error) {
    console.error("Error finding rules with filter:", error);
    throw error;
  }
};

// --- UPDATE an existing interaction rule ---
export const updateInteractionRule = async (ruleId: string, data: any) => {
  try {
    const updatedRule = await prisma.participantGroupInteractionRule.update({
      where: {
        id: ruleId,
      },
      data: data,
    });
    return updatedRule;
  } catch (error) {
    console.error("Error updating interaction rule:", error);
    throw error;
  }
};

// --- DELETE an interaction rule ---
export const deleteInteractionRule = async (ruleId: string) => {
  try {
    const deletedRule = await prisma.participantGroupInteractionRule.delete({
      where: {
        id: ruleId,
      },
    });
    return deletedRule;
  } catch (error) {
    console.error("Error deleting interaction rule:", error);
    throw error;
  }
};

// This replaces existing rules for the given (sourceGroupId, interactionType) with the provided target group types.
export const setInteractionRulesForTypes = async (
  eventId: string,
  sourceGroupId: string,
  interactionType: ParticipantInteractionType,
  effect: ParticipantInteractionEffect,
  targetGroupTypes: ParticipantGroupType[],
  privacy: NetworkingType
) => {
  try {
    if (!interactionType) {
      throw new Error("interactionType is required");
    }
    if (!Array.isArray(targetGroupTypes)) {
      throw new Error("targetGroupTypes must be an array");
    }
    // Resolve target group IDs by type within the event
    const targetGroups = await prisma.participantGroup.findMany({
      where: {
        eventId,
        type: { in: targetGroupTypes },
      },
      select: { id: true },
    });

    const targetGroupIds = targetGroups.map((g) => g.id);

    // Use a transaction to replace existing rules
    const result = await prisma.$transaction(async (tx) => {
      // Delete existing rules for this source and type
      await tx.participantGroupInteractionRule.deleteMany({
        where: {
          sourceGroupId,
          interactionType,
        },
      });

      if (targetGroupIds.length === 0) {
        return [] as const;
      }

      // Create new rules for each target group id
      const created = await Promise.all(
        targetGroupIds.map((targetGroupId) =>
          tx.participantGroupInteractionRule.create({
            data: {
              sourceGroupId,
              targetGroupId,
              interactionType,
              effect,
              networkingPrivacy: privacy,
            },
          })
        )
      );

      return created;
    });

    return result;
  } catch (error) {
    console.error("Error setting interaction rules by types:", error);
    throw error;
  }
};

type InteractionSummary = {
  canConnectWith: string[];
  canChatWith: string[];
  canBookMeetingWith: string[];
  canMatchmakeWith: string[];
  canAccessFloorPlan: boolean;
  canAccessBookmarks: boolean;
  canAccessAgenda: boolean;
  privacySettings: NetworkingType | null;
  canAccessPersonalDetails: boolean;
  canAccessMyBadge: boolean;
  canAccessMyAgenda: boolean;
  canAccessMyMeetings: boolean;
  canAccessMyConnections: boolean;
  canAccessAnalytics: boolean;
  canAccessNotifications: boolean;
  canAccessTeam: boolean;
  canAccessProducts: boolean;
  canAccessDocuments: boolean;
  canAccessVideos: boolean;
  canAccessCalendar: boolean;
  canAccessLeads: boolean;
  exhibitorPermissions: TeamMemberPermissions | undefined;
  canAccessBoothAdmin: boolean;
  isCompanyTeamMember: boolean;
  isCompanyOwner: boolean;
};

const getParticipantWithCompanyGroups = (userId: string, eventId: string) => {
  // Exhibitor/Sponsor(eventId, teamIds) indexes; `team: { some: { id } }` would $lookup User.
  const teamCompanyFilter = { eventId, teamIds: { has: userId } };
  return prisma.profile.findFirst({
    where: { userId, eventId },
    include: {
      user: {
        include: {
          exhibitors: {
            where: teamCompanyFilter,
            select: { participantGroupId: true },
          },
          sponsors: {
            where: teamCompanyFilter,
            select: { participantGroupId: true },
          },
        },
      },
    },
  });
};

export const getParticipantInteractionCapabilities = async (
  userId: string,
  eventSlug: string
) => {
  // Resolve the event first so the profile lookup hits Profile(eventId, userId)
  // instead of a relation filter ($lookup Event + COLLSCAN on Profile).
  const event = await prisma.event.findFirst({
    where: { slug: eventSlug },
    select: { id: true },
  });
  const participant = event
    ? await getParticipantWithCompanyGroups(userId, event.id)
    : null;
  const result: InteractionSummary = {
    canConnectWith: [],
    canChatWith: [],
    canBookMeetingWith: [],
    privacySettings: NetworkingType.BY_REQUEST,
    canMatchmakeWith: [],
    canAccessFloorPlan: false,
    canAccessBookmarks: false,
    canAccessAgenda: false,
    canAccessPersonalDetails: false,
    canAccessMyBadge: false,
    canAccessMyAgenda: false,
    canAccessMyMeetings: false,
    canAccessMyConnections: false,
    canAccessAnalytics: false,
    canAccessNotifications: false,
    canAccessTeam: false,
    canAccessProducts: false,
    canAccessDocuments: false,
    canAccessVideos: false,
    canAccessCalendar: false,
    canAccessLeads: false,
    exhibitorPermissions: participant?.exhibitorPermissions ?? undefined,
    canAccessBoothAdmin: false,
    isCompanyTeamMember: false,
    isCompanyOwner: false,
  };

  const eventId = participant?.eventId ?? event?.id;
  let companyGroupIds = [
    ...(participant?.user?.exhibitors ?? []).map((e) => e.participantGroupId),
    ...(participant?.user?.sponsors ?? []).map((s) => s.participantGroupId),
  ].filter((id): id is string => Boolean(id));

  if (companyGroupIds.length === 0 && eventId) {
    const [onExhibitorTeam, onSponsorTeam] = await Promise.all([
      prisma.exhibitor.findFirst({
        where: { eventId, teamIds: { has: userId } },
        select: { participantGroupId: true },
      }),
      prisma.sponsor.findFirst({
        where: { eventId, teamIds: { has: userId } },
        select: { participantGroupId: true },
      }),
    ]);
    companyGroupIds = [onExhibitorTeam?.participantGroupId, onSponsorTeam?.participantGroupId].filter(
      (id): id is string => Boolean(id)
    );
  }

  // Company team inherits rules from exhibitor/sponsor group, not visitor registration groups.
  const sourceGroupIds =
    companyGroupIds.length > 0
      ? companyGroupIds
      : (participant?.participantGroupIds ?? []);

  if (sourceGroupIds.length > 0) {
    // Scalars only — include: { targetGroup } is unused here and N+1s on Mongo.
    const rules = await prisma.participantGroupInteractionRule.findMany({
      where: { sourceGroupId: { in: sourceGroupIds } },
      select: {
        interactionType: true,
        effect: true,
        targetGroupId: true,
        networkingPrivacy: true,
      },
    });

    applyCapabilityRules(result, rules);
  }

  let companyType: Awaited<ReturnType<typeof resolveCompanyTypeForUser>> = null;
  if (eventId) {
    companyType = await resolveCompanyTypeForUser(userId, eventId);
    if (companyType) {
      result.isCompanyTeamMember = true;
      const ownerPermissions = await ensureCompanyOwnerPortalAccess(
        userId,
        eventId,
        companyType
      );
      if (ownerPermissions) {
        result.isCompanyOwner = true;
        result.exhibitorPermissions = ownerPermissions;
      }
      result.canAccessBoothAdmin = await canAccessBoothAdminPortal({
        userId,
        eventId,
        companyType,
        userEmail: participant?.user?.email ?? participant?.email,
      });
    }
  }

  // Company team members without event-day badge use portal tools only, not public attendee networking.
  if (
    companyType &&
    result.exhibitorPermissions?.eventDayBadge !== true
  ) {
    result.canConnectWith = [];
    result.canChatWith = [];
    result.canBookMeetingWith = [];
    result.canMatchmakeWith = [];
    result.canAccessPersonalDetails = false;
    result.canAccessMyBadge = false;
    result.canAccessMyAgenda = false;
    result.canAccessMyMeetings = false;
    result.canAccessMyConnections = false;
    result.canAccessFloorPlan = false;
    result.canAccessBookmarks = false;
    result.canAccessAgenda = false;
  }

  return result;
};
